// ============================================================================
// DOMAIN NODE - Collapsible domain group in entity tree
// ============================================================================

import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { ClassNode } from './ClassNode'
import type { EntityTreeClassGroup, EntityTreeInstance } from '@/lib/api/client'
import type { PhaseEntityRequirement } from '@/hooks/useEntityTree'
import type { EntityDomain } from '@ispot/shared'

interface DomainNodeProps {
  domain: EntityDomain
  classGroups: EntityTreeClassGroup[]
  phaseRequirements: PhaseEntityRequirement[]
  showProgress?: boolean
  showValidationActions?: boolean
  /** Enable inline entity creation */
  showInlineAdd?: boolean
  /** Enable AI suggestions button */
  showAISuggestions?: boolean
  onConfirm: (entityUri: string) => void
  onReject: (entityUri: string) => void
  onEntitySelect?: (entity: EntityTreeInstance) => void
  /** Callback when AI suggestions button is clicked */
  onAISuggestionsClick?: (classGroup: EntityTreeClassGroup) => void
  selectedClassUri?: string
  selectedEntityUri?: string
  /** Callback when a new entity is created inline */
  onCreateEntity?: (classUri: string, label: string) => Promise<void>
}

const DOMAIN_LABELS: Record<EntityDomain, string> = {
  market: 'Market',
  business: 'Business',
  solution: 'Solution',
}

const DOMAIN_COLORS: Record<EntityDomain, string> = {
  market: 'bg-market',
  business: 'bg-business',
  solution: 'bg-solution',
}

export function DomainNode({
  domain,
  classGroups,
  phaseRequirements,
  showProgress = true,
  showValidationActions = true,
  showInlineAdd = true,
  showAISuggestions = false,
  onConfirm,
  onReject,
  onEntitySelect,
  onAISuggestionsClick,
  selectedClassUri,
  selectedEntityUri,
  onCreateEntity,
}: DomainNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  // Calculate total instance count for this domain
  const totalCount = classGroups.reduce(
    (sum, group) => sum + group.instances.length,
    0
  )

  // Get phase requirements for this domain
  const domainRequirements = phaseRequirements.filter((r) => r.domain === domain)

  // Don't render if no classes in this domain
  if (classGroups.length === 0 && domainRequirements.length === 0) {
    return (
      <div className="mb-1">
        <div className="flex items-center gap-2 px-2.5 py-2 rounded-md hover:bg-warm-50 cursor-pointer">
          <ChevronRight className="w-4 h-4 text-warm-400" />
          <span className={`w-2.5 h-2.5 rounded-full ${DOMAIN_COLORS[domain]}`} />
          <span className="flex-1 text-sm font-medium text-warm-700">
            {DOMAIN_LABELS[domain]}
          </span>
          <span className="text-xs text-warm-400 bg-warm-100 px-1.5 py-0.5 rounded-full">
            0
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-1">
      {/* Domain header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md hover:bg-warm-50 transition-colors"
      >
        <ChevronRight
          className={`w-4 h-4 text-warm-400 transition-transform ${
            isExpanded ? 'rotate-90' : ''
          }`}
        />
        <span className={`w-2.5 h-2.5 rounded-full ${DOMAIN_COLORS[domain]}`} />
        <span className="flex-1 text-left text-sm font-medium text-warm-700">
          {DOMAIN_LABELS[domain]}
        </span>
        <span className="text-xs text-warm-400 bg-warm-100 px-1.5 py-0.5 rounded-full">
          {totalCount}
        </span>
      </button>

      {/* Class groups */}
      {isExpanded && (
        <div className="ml-6">
          {classGroups.map((classGroup) => {
            // Find matching requirement for progress display
            const requirement = domainRequirements.find(
              (r) => r.classUri === classGroup.classUri
            )

            return (
              <ClassNode
                key={classGroup.classUri}
                classGroup={classGroup}
                requirement={requirement}
                showProgress={showProgress}
                showValidationActions={showValidationActions}
                showInlineAdd={showInlineAdd}
                showAISuggestions={showAISuggestions}
                onConfirm={onConfirm}
                onReject={onReject}
                onEntitySelect={onEntitySelect}
                onAISuggestionsClick={onAISuggestionsClick}
                selectedClassUri={selectedClassUri}
                selectedEntityUri={selectedEntityUri}
                onCreateEntity={onCreateEntity}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

export default DomainNode
